/**
 * SSE Processor for TipTap Document Agent
 *
 * Handles parsing and processing of SSE events from the TipTap agent backend.
 * Uses TipTap patches instead of HTML-based document updates.
 */

import {
  TipTapPatch,
  TipTapToolOutput,
  ToolCallSSEMessage,
  ToolResultSSEMessage,
  ChatMessage,
} from "../DocumentAgentLGTypes";
import { handleFetchUnauthorized } from "../../../Utils/handleFetchUnauthorized";

// ============================================================================
// TYPES
// ============================================================================

interface TextSSEMessage {
  text: string;
}

interface CreatedDocumentGeneric {
  docId: string;
  documentType: string;
  documentName?: string;
  content?: any;
  formData?: any;
  meta?: Record<string, any>;
  gcsPath?: string;
  formSchema?: any;
  pdfGcsPath?: string;
  ocrGcsPath?: string;
  ocrMetadata?: any;
}

interface NormalizedToolOutput {
  ok: boolean;
  createdDocuments: CreatedDocumentGeneric[];
  patch?: TipTapPatch;
  patchExternalRef?: {
    type?: string;
    bucket: string;
    object: string;
  };
}

export interface SSEProcessorTipTapCallbacks {
  // Message handling
  onMessage: (message: ChatMessage) => void;
  onTextChunk: (chunk: string) => void;
  onScrollCheck: () => void;

  // Thinking/reasoning handling (for models with extended thinking)
  onThinkingStart?: () => void;
  onThinkingChunk?: (chunk: string) => void;
  onThinkingComplete?: (durationSeconds: number) => void;

  // Error handling
  onError?: (error: string) => void;

  // Context-usage gauge update (emitted at the end of each turn)
  onContextUsage?: (usage: {
    contextTokens: number;
    maxContextTokens: number;
  }) => void;

  // Tool handling
  onToolCall: (data: ToolCallSSEMessage) => void;
  onToolResult: (data: ToolResultSSEMessage) => void;

  // TipTap-specific: patch application
  applyPatch: (patch: TipTapPatch) => any;

  // Document management
  addTipTapDocument: (
    docId: string,
    documentName: string,
    content: any,
    editable?: boolean
  ) => void;
  addPDFDocument?: (
    docId: string,
    documentName: string,
    pdfGcsPath: string,
    ocrGcsPath: string,
    ocrMetadata: any
  ) => void;
  addFormDocument?: (
    docId: string,
    documentName: string,
    formData: any,
    isMutable: boolean,
    gcsPath?: string,
    formSchema?: any,
    initialDirty?: boolean,
    meta?: Record<string, any>
  ) => void;
  updateFormData?: (docId: string, formData: any) => void;
  getDocumentById?: (docId: string) => any;
  saveDirtyDocuments?: () => Promise<{ saved: string[]; failed: string[] }>;

  // UI callbacks
  onDocumentActivated?: (docId: string, documentName: string) => void;
  translate?: (key: string, params?: Record<string, any>) => string;

  // Optional workflow hooks
  shouldHandleTool?: (toolName: string) => boolean;

  // Persist-first patch architecture: session ID for REST patch fetching
  getSessionId?: () => string | undefined;

  /**
   * Override the patches REST endpoint path (e.g. "/api/patent-agent/patches").
   * Defaults to "/api/document-agent/patches" if not provided.
   */
  patchesEndpoint?: string;

  /** Workproduct ID for access validation on patch fetch requests. */
  workproductId?: string;
}

export interface SSEProcessorTipTapOptions {
  /** Initial patch sequence number to start from (skip patches ≤ this value).
   *  Use to resume from a previous message's last applied seq within the same chat. */
  initialPatchSeq?: number;
}

export interface SSEProcessorTipTap {
  processChunk: (text: string) => void;
  clearCache: () => void;
  /** Fetch and apply any patches from the server after the given sequence number.
   *  Used by the fallback polling mechanism and on SSE nudge. */
  fetchAndApplyPatches: (sessionId: string, afterSeq?: number) => Promise<void>;
  /** Get the highest patch sequence number that has been applied. */
  getLastAppliedSeq: () => number;
  /** Reset the lastAppliedSeq (e.g. when switching to a different chat session). */
  resetPatchSeq: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

let autoUpdateCounter = 0;
function generateUniqueId(prefix: string): string {
  return `${prefix}_${Date.now()}_${++autoUpdateCounter}`;
}

function coerceObject<T = any>(raw: unknown): T | null {
  if (raw == null) return null as any;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null as any;
    }
  }
  if (typeof raw === "object") return raw as T;
  return null as any;
}

function normalizeToolOutput(rawOutput: unknown): NormalizedToolOutput {
  const obj = coerceObject<any>(rawOutput) || {};
  const ok = obj.ok !== false && obj.success !== false;

  // Support both single and plural payloads
  const single: CreatedDocumentGeneric | undefined = obj.createdDocument;
  const plural: CreatedDocumentGeneric[] | undefined = obj.createdDocuments;

  const createdDocuments: CreatedDocumentGeneric[] = Array.isArray(plural)
    ? plural
    : single
      ? [single]
      : [];

  return {
    ok,
    createdDocuments,
    patch: obj.patch,
    patchExternalRef: obj.patchExternalRef,
  };
}

// Type guards
function isTextSSEMessage(value: unknown): value is TextSSEMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as any).text === "string"
  );
}

function isToolCallMessage(value: unknown): value is ToolCallSSEMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    ((value as any).toolCalled === true ||
      (value as any).toolStarted === true) &&
    typeof (value as any).toolName === "string"
  );
}

function isToolResultMessage(value: unknown): value is ToolResultSSEMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as any).toolCompleted === true &&
    typeof (value as any).toolName === "string"
  );
}

function isErrorMessage(
  value: unknown
): value is { type: "error"; error: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    ((value as any).type === "error" ||
      typeof (value as any).error === "string")
  );
}

// ============================================================================
// FACTORY
// ============================================================================

export function createSSEProcessorTipTap(
  callbacks: SSEProcessorTipTapCallbacks,
  options?: SSEProcessorTipTapOptions
): SSEProcessorTipTap {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  // Private state
  let buffer = "";
  // SSE parse state — must persist across processChunk calls so that
  // event: / data: lines split across HTTP chunks are reassembled correctly.
  let currentEvent = "";
  let currentData = "";
  let _documentContentCache: Record<string, any> = {};
  let _figuresDataCache: Record<string, any[]> = {};

  // Persist-first patch architecture state
  // Initialize from options so we can resume from a previous message's last seq
  let lastAppliedSeq = options?.initialPatchSeq ?? 0;
  // Serializes overlapping patch fetches: each call chains behind the previous
  // so they run to completion IN ORDER instead of a later caller no-op'ing out.
  let patchFetchChain: Promise<void> = Promise.resolve();

  /**
   * Fetch patches from the REST endpoint and apply them in order.
   * This is called both on SSE nudge (patchAvailable) and by fallback polling.
   *
   * Overlapping calls are SERIALIZED via patchFetchChain rather than dropped:
   * the previous no-op-return meant the stream-end "final fetch" could skip
   * while an in-flight fetch was still applying the last patches, so the next
   * message re-fetched and re-applied them (duplicated content). Chaining makes
   * every caller await the prior fetch and then run with an up-to-date seq.
   */
  function fetchAndApplyPatches(
    sessionId: string,
    afterSeq?: number
  ): Promise<void> {
    const run = patchFetchChain.then(() =>
      doFetchAndApplyPatches(sessionId, afterSeq)
    );
    // Keep the chain alive even if a fetch rejects, so later fetches still run.
    patchFetchChain = run.catch(() => {});
    return run;
  }

  async function doFetchAndApplyPatches(
    sessionId: string,
    afterSeq?: number
  ): Promise<void> {
    const fetchAfterSeq = afterSeq !== undefined ? afterSeq : lastAppliedSeq;

    const patchesPath = callbacks.patchesEndpoint || "/api/document-agent/patches";
    try {
      const wpParam = callbacks.workproductId ? `&workproductId=${encodeURIComponent(callbacks.workproductId)}` : "";
      const res = await fetch(
        `${backendUrl}${patchesPath}?sessionId=${encodeURIComponent(sessionId)}&afterSeq=${fetchAfterSeq}${wpParam}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        if (handleFetchUnauthorized(res)) return;
        console.error("PatchFetch: server returned", res.status);
        return;
      }
      const data = await res.json();
      const patches: Array<{
        seqNumber: number;
        toolOutput: any;
        toolName: string;
        callId: string;
      }> = data.patches || [];

      for (const p of patches) {
        // Skip patches we've already applied
        if (p.seqNumber <= lastAppliedSeq) continue;

        const toolOutput = p.toolOutput;

        // Apply TipTap patches
        if (toolOutput.patches && toolOutput.patches.length > 0) {
          for (const patch of toolOutput.patches) {
            handleTipTapPatch(patch);
          }
        } else if (toolOutput.patch) {
          handleTipTapPatch(toolOutput.patch);
        }

        // Apply figuresData updates and createdDocuments
        processNonPatchToolOutput(toolOutput);

        lastAppliedSeq = Math.max(lastAppliedSeq, p.seqNumber);
      }
    } catch (error) {
      console.error("PatchFetch: failed to fetch patches:", error);
    }
  }

  /**
   * Handle TipTap patch application
   */
  function handleTipTapPatch(patch: TipTapPatch): void {
    try {
      const result = callbacks.applyPatch(patch);

      if (result?.success) {
        // Activate the document
        if (callbacks.onDocumentActivated) {
          // Get document name from context if available
          const doc = callbacks.getDocumentById?.(patch.documentId);
          const documentName = doc?.documentName || "Document";
          callbacks.onDocumentActivated(patch.documentId, documentName);
        }

        // Note: No separate system notification needed here.
        // The tool result message (from onToolResult) already shows
        // a concise summary like 'Applied 3 operation(s) to document "..."'.
      } else {
      }
    } catch (error) {
      console.error("Error applying TipTap patch:", error);
    }
  }

  /**
   * Process figuresData updates and createdDocuments from a tool output.
   * Shared between inline SSE handling (handleDocumentUpdate) and
   * persist-first DB fetch (fetchAndApplyPatches).
   */
  function processNonPatchToolOutput(toolOutput: TipTapToolOutput): void {
    // Handle figure metadata updates (e.g. generate_patent_figure on an existing
    // Figures doc). Purpose: immediate in-session panel refresh without page reload.
    //
    // Contract (backend tool output):
    // - figuresUpdated.docId: target form document id
    // - figuresData: latest full figure list for that document
    //
    // Pattern is reusable for other agent-specific panels as well.
    const figuresUpdated = (toolOutput as any).figuresUpdated as
      | { docId?: string }
      | undefined;
    const figuresData = (toolOutput as any).figuresData as any[] | undefined;
    if (
      figuresUpdated?.docId &&
      Array.isArray(figuresData) &&
      callbacks.updateFormData
    ) {
      const existing = callbacks.getDocumentById?.(figuresUpdated.docId);
      if (existing && existing.documentType === "form_mutable") {
        callbacks.updateFormData(figuresUpdated.docId, figuresData);
      } else if (callbacks.addFormDocument) {
        // Fallback for safety: if the target form doc isn't in local context yet,
        // create it so the panel can render immediately.
        callbacks.addFormDocument(
          figuresUpdated.docId,
          "Figures",
          figuresData,
          true,
          undefined,
          undefined,
          true,
          { panelType: "figures" }
        );
      }
      if (callbacks.saveDirtyDocuments) {
        // Save after a short delay to avoid React state update races.
        setTimeout(() => {
          void callbacks.saveDirtyDocuments?.().catch(err => {
            console.error("Failed to persist figure metadata update:", err);
          });
        }, 150);
      }
    }

    // Handle created documents
    const { ok: _ok, createdDocuments, patchExternalRef } =
      normalizeToolOutput(toolOutput);

    // If there's an external patch reference, it will be handled by the fetch logic
    if (patchExternalRef) return;

    // Always process createdDocuments even if the tool reports !ok,
    // because the document itself was created (e.g., generate_patent_figure creates
    // the Figures document even if image generation later fails).
    if (!createdDocuments.length) return;

    let createdAny = false;

    for (const created of createdDocuments) {
      const exists = callbacks.getDocumentById?.(created.docId);
      if (exists) continue;

      // Handle form documents
      if (
        (created.documentType === "form_mutable" ||
          created.documentType === "form_immutable") &&
        callbacks.addFormDocument
      ) {
        const isMutable = created.documentType !== "form_immutable";
        callbacks.addFormDocument(
          created.docId,
          created.documentName || "Form Document",
          created.formData || {},
          isMutable,
          created.gcsPath,
          created.formSchema,
          true,
          created.meta
        );
        createdAny = true;
        if (callbacks.onDocumentActivated) {
          callbacks.onDocumentActivated(
            created.docId,
            created.documentName || "Form Document"
          );
        }
        continue;
      }

      // Handle PDF documents
      if (created.documentType === "pdf" && callbacks.addPDFDocument) {
        callbacks.addPDFDocument(
          created.docId,
          created.documentName || "PDF Document",
          created.pdfGcsPath || "",
          created.ocrGcsPath || "",
          created.ocrMetadata || {
            hasText: false,
            hasImages: false,
            pageCount: 0,
            lastProcessed: new Date(),
          }
        );
        createdAny = true;
        if (callbacks.onDocumentActivated) {
          callbacks.onDocumentActivated(
            created.docId,
            created.documentName || "PDF Document"
          );
        }
        continue;
      }

      // Handle TipTap documents
      if (created.documentType === "tiptap") {
        callbacks.addTipTapDocument(
          created.docId,
          created.documentName || "Document",
          created.content || { type: "doc", content: [] },
          true
        );
        createdAny = true;
        if (callbacks.onDocumentActivated) {
          callbacks.onDocumentActivated(
            created.docId,
            created.documentName || "Document"
          );
        }
        continue;
      }
    }

    // Persist newly-created docs shortly after state updates flush.
    // addFormDocument/addTipTapDocument update React state; saving in the
    // same tick can race and miss the new dirty doc.
    if (createdAny && callbacks.saveDirtyDocuments) {
      setTimeout(() => {
        void callbacks.saveDirtyDocuments?.().catch(err => {
          console.error("Failed to persist created documents:", err);
        });
      }, 150);
    }
  }

  /**
   * Handle document update from tool result (inline SSE path)
   */
  function handleDocumentUpdate(parsed: ToolResultSSEMessage): void {
    const toolOutput = (parsed as any).toolOutput as
      | TipTapToolOutput
      | undefined;
    if (!toolOutput) return;

    // Handle TipTap patches (multi-doc: apply all, single-doc: apply the one)
    if (toolOutput.patches && toolOutput.patches.length > 0) {
      for (const p of toolOutput.patches) {
        handleTipTapPatch(p);
      }
    }
    if (toolOutput.patch) {
      handleTipTapPatch(toolOutput.patch);
    }

    // Handle figuresData + createdDocuments
    processNonPatchToolOutput(toolOutput);
  }

  /**
   * Process a complete SSE message event
   */
  function processMessageEvent(data: string): void {
    // Skip [DONE] signal — it's not JSON
    if (data === "[DONE]") {
      return;
    }

    try {
      const parsed: unknown = JSON.parse(data);

      // Handle context-usage gauge updates (sent at end of turn)
      if (
        (parsed as any)?.type === "contextUsage" &&
        (parsed as any).contextUsage
      ) {
        const cu = (parsed as any).contextUsage;
        if (
          typeof cu.contextTokens === "number" &&
          typeof cu.maxContextTokens === "number"
        ) {
          callbacks.onContextUsage?.(cu);
        }
        return;
      }

      // Handle error events from backend
      if (isErrorMessage(parsed)) {
        const errorMsg =
          (parsed as any).error || (parsed as any).message || "Unknown error";
        const errorCode = (parsed as any).code;
        console.error("Backend error:", errorMsg);

        if (callbacks.onError) {
          callbacks.onError(errorMsg);
        }

        // Recursion limit: show a friendly continue button instead of an error
        if (errorCode === "RECURSION_LIMIT") {
          const question = callbacks.translate
            ? callbacks.translate("agent.recursionLimit.question")
            : "The task needs more steps to complete. How would you like to proceed?";
          const continueLabel = callbacks.translate
            ? callbacks.translate("agent.recursionLimit.continue")
            : "Continue";
          const stopLabel = callbacks.translate
            ? callbacks.translate("agent.recursionLimit.stop")
            : "Stop here";

          const choiceData = {
            id: `recursion_continue_${Date.now()}`,
            question,
            options: [
              { id: "continue", label: continueLabel },
              { id: "stop", label: stopLabel },
            ],
            allowMultiple: false,
            allowCustom: true,
            customPlaceholder: callbacks.translate
              ? callbacks.translate("agent.recursionLimit.customPlaceholder")
              : "Or type a specific instruction...",
          };

          const xmlContent = `<multiple-choice>\n${JSON.stringify(choiceData, null, 2)}\n</multiple-choice>`;
          callbacks.onMessage({
            id: generateUniqueId("recursion_choice"),
            role: "assistant",
            content: xmlContent,
            timestamp: new Date(),
          });
          return;
        }

        // For quota exceeded, don't label it as "Error" and add a purchase link marker
        const isQuotaExceeded = errorCode === "QUOTA_EXCEEDED";
        const translatedMsg = isQuotaExceeded && callbacks.translate
          ? callbacks.translate("displayspecificworktypepage.monthlyTokenLimitReached")
          : errorMsg;
        const content = isQuotaExceeded
          ? `⚠️ ${translatedMsg} {{PURCHASE_LINK}}`
          : `⚠️ Error: ${errorMsg}`;

        // Also display as a system message
        const errorMessage: ChatMessage = {
          id: generateUniqueId("error"),
          role: "system",
          content,
          timestamp: new Date(),
        };
        callbacks.onMessage(errorMessage);
        return;
      }

      if (isToolCallMessage(parsed)) {
        callbacks.onToolCall(parsed);
        return;
      }

      if (isToolResultMessage(parsed)) {
        const toolOutput: any = (parsed as any).toolOutput || {};
        const ref: any = toolOutput?.patchExternalRef;

        // --- Persist-first: handle patchAvailable nudge (preferred path) ---
        if ((parsed as any).patchAvailable && (parsed as any).sessionId) {
          const sessionId = (parsed as any).sessionId as string;
          // Show tool result in chat UI immediately (stripped output, no patch data)
          callbacks.onToolResult(parsed as ToolResultSSEMessage);
          // Fetch the actual patches from the REST endpoint
          fetchAndApplyPatches(sessionId).then(() => {
            callbacks.onScrollCheck();
          });
          return;
        }

        // Handle external patch references (GCS) — legacy path
        if (ref && ref.type === "gcs") {
          fetch(`${backendUrl}/api/document-agent/get-patch`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ object: ref.object }),
          })
            .then(r => {
              if (!r.ok) throw new Error("server_fetch_failed");
              return r.json();
            })
            .then(obj => {
              (parsed as any).toolOutput = {
                ...toolOutput,
                patch: obj.patch,
                docId: obj.docId || toolOutput.docId,
                documentName: obj.documentName || toolOutput.documentName,
              };
              callbacks.onToolResult(parsed as ToolResultSSEMessage);
              handleDocumentUpdate(parsed as ToolResultSSEMessage);
              callbacks.onScrollCheck();
            })
            .catch(() => {
              const errMsg: ChatMessage = {
                id: `tool_error_${Date.now()}`,
                role: "tool",
                content: JSON.stringify({
                  action: "tool_error",
                  toolName: (parsed as any).toolName,
                  message: "Failed to download large patch. Please retry.",
                }),
                timestamp: new Date(),
              };
              callbacks.onMessage(errMsg);
            });
          return;
        }

        // Inline patches (fallback if DB save failed on backend, or non-LG agents)
        callbacks.onToolResult(parsed as ToolResultSSEMessage);
        handleDocumentUpdate(parsed as ToolResultSSEMessage);
        callbacks.onScrollCheck();
        return;
      }

      // Handle thinking/reasoning events
      if (
        typeof (parsed as any).thinkingStart === "boolean" &&
        (parsed as any).thinkingStart
      ) {
        callbacks.onThinkingStart?.();
        return;
      }

      if (typeof (parsed as any).thinking === "string") {
        const thinkingText = (parsed as any).thinking;
        if (thinkingText.length > 0) {
          callbacks.onThinkingChunk?.(thinkingText);
        }
        return;
      }

      if (
        typeof (parsed as any).thinkingComplete === "boolean" &&
        (parsed as any).thinkingComplete
      ) {
        const duration = (parsed as any).thinkingDuration || 0;
        callbacks.onThinkingComplete?.(duration);
        return;
      }

      // Handle plain text streaming
      if (isTextSSEMessage(parsed)) {
        const text = (parsed as any).text ?? "";
        if (typeof text === "string" && text.length > 0) {
          callbacks.onTextChunk(text);
        }
        return;
      }
    } catch (_e) {
    }
  }

  // Return public interface
  return {
    processChunk(text: string): void {
      buffer += text;
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7);
        } else if (line.startsWith("data: ")) {
          currentData = line.slice(6);
        } else if (line === "") {
          if (currentEvent === "message" && currentData) {
            processMessageEvent(currentData);
          }
          currentEvent = "";
          currentData = "";
        }
      }
    },

    clearCache(): void {
      _documentContentCache = {};
      _figuresDataCache = {};
    },

    // Persist-first patch architecture: exposed for fallback polling
    fetchAndApplyPatches,
    getLastAppliedSeq: () => lastAppliedSeq,
    resetPatchSeq: () => {
      lastAppliedSeq = 0;
    },
  };
}
